import React, { useCallback, useRef, useState } from 'react'
import { getBrand } from '../../utils/brand'
import { ropcLogin, completeFirstLogin, AuthError, RESET_PASSWORD_URL } from '../../services/auth.service'
import apexLogo from '../../assets/login/apex-logo.png'
import imperiumLogo from '../../assets/login/imperium-logo.png'
import portilioLogo from '../../assets/login/portilio-logo.png'
import './LoginScreen.css'

const BRANDS = {
  apex: {
    logo: apexLogo,
    logoAlt: 'Apex Capital',
    motto: 'They think, we know.',
  },
  imperium: {
    logo: imperiumLogo,
    // Jediný povolený formát názvu společnosti je verzálkami (pokyn V. Kosáček)
    logoAlt: 'IMPERIUM FINANCE',
    motto: null,
  },
}

const UserIcon = () => (
  <svg className="lg-ic" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

const LockIcon = ({ className, size }: { className?: string; size?: number }) => (
  <svg className={className} width={size || 17} height={size || 17} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
)

const EyeIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const Spinner = () => (
  <svg className="lg-btnspin" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <path d="M12 2a10 10 0 0 1 10 10" />
  </svg>
)

// Guilloché rozeta (Imperium) — deterministické elipsy dle mockupu
const GUILLOCHE_FAMILIES = [
  { rx: 182, ry: 72, count: 64, stroke: '#C9A86A', w: 0.6 },
  { rx: 126, ry: 126, count: 48, stroke: '#9BB6D6', w: 0.5 },
  { rx: 154, ry: 46, count: 78, stroke: '#C9A86A', w: 0.5 },
]

const Guilloche = () => (
  <svg className="lg-guilloche" viewBox="-200 -200 400 400" aria-hidden="true">
    {GUILLOCHE_FAMILIES.map((f, fi) =>
      Array.from({ length: f.count }, (_, i) => (
        <ellipse
          key={`${fi}-${i}`}
          cx={0}
          cy={0}
          rx={f.rx}
          ry={f.ry}
          fill="none"
          stroke={f.stroke}
          strokeWidth={f.w}
          transform={`rotate(${((i / f.count) * 360).toFixed(2)})`}
        />
      ))
    )}
  </svg>
)

const ApexBackground = () => (
  <>
    <div className="lg-aurora"><b className="lg-b1" /><b className="lg-b2" /><b className="lg-b3" /></div>
    <div className="lg-grid" />
    <div className="lg-halo" />
    <div className="lg-tri1">
      <svg width="460" height="460" viewBox="0 0 460 460">
        <polygon points="230,40 420,420 40,420" fill="none" stroke="rgba(99,150,230,.26)" strokeWidth="1.5" />
        <circle r="3.5" fill="rgba(120,175,255,.95)">
          <animateMotion dur="22s" repeatCount="indefinite" path="M230,40 L420,420 L40,420 Z" />
        </circle>
      </svg>
    </div>
    <div className="lg-tri2">
      <svg width="560" height="560" viewBox="0 0 560 560">
        <polygon points="280,50 510,510 50,510" fill="none" stroke="rgba(126,166,244,.2)" strokeWidth="1.5" />
        <circle r="3.5" fill="rgba(120,175,255,.9)">
          <animateMotion dur="28s" repeatCount="indefinite" path="M280,50 L510,510 L50,510 Z" />
        </circle>
      </svg>
    </div>
    <div className="lg-orbit1"><div className="lg-dot" /></div>
    <div className="lg-orbit2" />
    <div className="lg-chart">
      <svg width="100%" height="100%" viewBox="0 0 1440 400" preserveAspectRatio="none">
        <polyline points="0,340 180,300 340,320 520,240 700,270 900,170 1060,205 1240,110 1440,60" fill="none" stroke="rgba(59,130,246,.24)" strokeWidth="2" />
        <polyline points="0,370 200,350 380,360 560,315 760,330 960,270 1140,290 1320,230 1440,200" fill="none" stroke="rgba(126,166,244,.12)" strokeWidth="2" />
      </svg>
    </div>
  </>
)

const ImperiumBackground = () => (
  <>
    <div className="lg-aurora"><b className="lg-b1" /><b className="lg-b2" /><b className="lg-b3" /></div>
    <div className="lg-halo" />
    <Guilloche />
    <div className="lg-ring lg-ring1"><span className="lg-dot" /></div>
    <div className="lg-ring lg-ring2" />
  </>
)

const revealStyle = (i: number) => ({ '--lg-i': i } as React.CSSProperties)

const LoginScreen = () => {
  const brand = getBrand()
  const cfg = BRANDS[brand]
  const cardRef = useRef<HTMLDivElement>(null)

  const [view, setView] = useState<'login' | 'newPassword'>('login')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [newPassword2, setNewPassword2] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onCardPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const card = cardRef.current
    if (!card) return
    const r = card.getBoundingClientRect()
    card.style.setProperty('--lg-mx', `${(((e.clientX - r.left) / r.width) * 100).toFixed(1)}%`)
    card.style.setProperty('--lg-my', `${(((e.clientY - r.top) / r.height) * 100).toFixed(1)}%`)
  }, [])

  // Po přihlášení plná navigace na root appky — init v index.tsx si tokeny
  // načte z localStorage a keycloak-js nastartuje jako přihlášený, bez
  // redirectu na Keycloak. Nesmí to být reload /login: ta cesta na Apache
  // bez SPA fallbacku vrací 404.
  const finishLogin = () => {
    window.location.assign(process.env.PUBLIC_URL || '/')
  }

  const submitLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return
    setError(null)
    setLoading(true)
    try {
      await ropcLogin(username.trim(), password, rememberMe)
      finishLogin()
    } catch (err) {
      setLoading(false)
      if (err instanceof AuthError && err.kind === 'update_password') {
        setError(null)
        setShowPassword(false)
        setView('newPassword')
      } else {
        setError(err instanceof AuthError ? err.message : 'Přihlášení se nezdařilo. Zkuste to prosím znovu.')
      }
    }
  }

  const submitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return
    if (newPassword.length < 8) {
      setError('Heslo musí mít alespoň 8 znaků.')
      return
    }
    if (newPassword !== newPassword2) {
      setError('Hesla se neshodují.')
      return
    }
    setError(null)
    setLoading(true)
    try {
      await completeFirstLogin(username.trim(), password, newPassword, rememberMe)
      finishLogin()
    } catch (err) {
      setLoading(false)
      setError(err instanceof AuthError ? err.message : 'Nastavení hesla se nezdařilo. Zkuste to prosím znovu.')
    }
  }

  const passwordInput = (
    id: string,
    value: string,
    onChange: (v: string) => void,
    autoComplete: string,
    placeholder: string
  ) => (
    <div className="lg-in">
      <LockIcon className="lg-ic" />
      <input
        className="lg-input lg-pw"
        id={id}
        type={showPassword ? 'text' : 'password'}
        placeholder={placeholder}
        autoComplete={autoComplete}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={loading}
      />
      <button
        className="lg-eye"
        type="button"
        onClick={() => setShowPassword(s => !s)}
        aria-label={showPassword ? 'Skrýt heslo' : 'Zobrazit heslo'}
      >
        <EyeIcon />
      </button>
    </div>
  )

  const errorBox = error && (
    <div className="lg-error" role="alert">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4" />
        <path d="M12 16h.01" />
      </svg>
      <span>{error}</span>
    </div>
  )

  return (
    <div className="lgate" data-brand={brand}>
      {brand === 'apex' ? <ApexBackground /> : <ImperiumBackground />}
      <div className="lg-vig" />
      <div className="lg-grain" />

      <div className="lg-center">
        <div className="lg-cardwrap">
          <div className="lg-card" ref={cardRef} onPointerMove={onCardPointerMove}>
            <div className="lg-spot" />
            <div className="lg-topbar" />
            <div className="lg-hero lg-r" style={revealStyle(0)}>
              <img src={cfg.logo} alt={cfg.logoAlt} />
            </div>
            <div className="lg-divider lg-r" style={revealStyle(1)}>
              <span className="lg-ln" /><span className="lg-dm" /><span className="lg-ln lg-ln2" />
            </div>

            {view === 'login' ? (
              <>
                <h1 className="lg-title lg-r" style={revealStyle(2)}>Přihlášení do klientské zóny</h1>
                <form onSubmit={submitLogin}>
                  <div className="lg-r" style={revealStyle(4)}>
                    <label className="lg-label" htmlFor="lg-username">Přihlašovací jméno nebo e-mail</label>
                    <div className="lg-in">
                      <UserIcon />
                      <input
                        className="lg-input"
                        id="lg-username"
                        type="text"
                        placeholder="jmeno@spolecnost.cz"
                        autoComplete="username"
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                        disabled={loading}
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="lg-r" style={revealStyle(5)}>
                    <div className="lg-labelrow">
                      <label className="lg-label" htmlFor="lg-password">Heslo</label>
                      <a href={RESET_PASSWORD_URL}>Zapomenuté heslo?</a>
                    </div>
                    {passwordInput('lg-password', password, setPassword, 'current-password', 'Zadejte heslo')}
                  </div>
                  <div className="lg-cbrow lg-r" style={revealStyle(6)}>
                    <label className="lg-cb">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={e => setRememberMe(e.target.checked)}
                        disabled={loading}
                      />{' '}
                      Zůstat přihlášen
                    </label>
                  </div>
                  {errorBox}
                  <button className="lg-btn lg-r" style={revealStyle(7)} type="submit" disabled={loading}>
                    {loading ? <Spinner /> : null}
                    Přihlásit se
                    {!loading && brand === 'apex' && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14" />
                        <path d="m13 6 6 6-6 6" />
                      </svg>
                    )}
                  </button>
                </form>
              </>
            ) : (
              <>
                <h1 className="lg-title lg-r" style={revealStyle(2)}>Nastavte si nové heslo</h1>
                <p className="lg-sub lg-r" style={revealStyle(3)}>
                  Přihlásili jste se dočasným heslem. Z bezpečnostních důvodů si prosím zvolte nové heslo.
                </p>
                <form onSubmit={submitNewPassword}>
                  <div className="lg-r" style={revealStyle(4)}>
                    <label className="lg-label" htmlFor="lg-newpassword">Nové heslo</label>
                    {passwordInput('lg-newpassword', newPassword, setNewPassword, 'new-password', 'Alespoň 8 znaků')}
                  </div>
                  <div className="lg-r" style={revealStyle(5)}>
                    <label className="lg-label" htmlFor="lg-newpassword2">Nové heslo znovu</label>
                    {passwordInput('lg-newpassword2', newPassword2, setNewPassword2, 'new-password', 'Zopakujte nové heslo')}
                  </div>
                  {errorBox}
                  <button className="lg-btn lg-r" style={revealStyle(6)} type="submit" disabled={loading}>
                    {loading ? <Spinner /> : null}
                    Nastavit heslo a přihlásit se
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        {cfg.motto && (
          <div className="lg-below">
            <div className="lg-motto">
              <span className="lg-ln" /><span className="lg-tx">{cfg.motto}</span><span className="lg-ln lg-ln2" />
            </div>
          </div>
        )}
      </div>

      <div className="lg-foot">
        {brand === 'apex' ? (
          <div>© {new Date().getFullYear()} Apex Capital, a.s.</div>
        ) : (
          <span className="lg-est">IMPERIUM FINANCE · MMXXVI</span>
        )}
        <div className="lg-pf">
          Běží na platformě <img src={portilioLogo} alt="Portilio" />
        </div>
      </div>
    </div>
  )
}

export default LoginScreen
